import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import {
  fetchUserLocations,
  fetchLocationData,
  clearError as clearErrorAction,
  LocationState
} from '../store/slices/locationSlice';
export const useLocation = () => {
  const dispatch = useAppDispatch();
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  
  // Get the location state from Redux
  const {
    locations,
    uniqueCountries,
    isLoading: storeLoading,
    error: storeError
  } = useAppSelector((state) => state.location || { 
    locations: [], 
    uniqueCountries: [], 
    isLoading: false, 
    error: null 
  } as LocationState);
  
  // Clear error
  const clearError = useCallback(() => {
    setLocalError(null);
    dispatch(clearErrorAction());
  }, [dispatch]);
  
  // Get user's training locations
  const getUserLocations = useCallback(async (userId: string) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const resultAction = await dispatch(fetchUserLocations(userId));
      const locations = fetchUserLocations.fulfilled.match(resultAction)
        ? resultAction.payload.locations
        : [];
      
      setLocalLoading(false);
      return locations;
    } catch (err: any) {
      setLocalLoading(false);
      setLocalError(err.message || 'Failed to load locations');
      return [];
    }
  }, [dispatch]);
  
  // Get specific location data
  const getLocationById = useCallback(async (placeId: string) => {
    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const resultAction = await dispatch(fetchLocationData(placeId));
      const location = fetchLocationData.fulfilled.match(resultAction)
        ? resultAction.payload
        : null;
      
      setLocalLoading(false);
      return location;
    } catch (err: any) {
      setLocalLoading(false);
      setLocalError(err.message || 'Failed to load location');
      return null;
    }
  }, [dispatch]);
  
  // Get user's current geolocation using Capacitor plugin (native) or browser API (web)
  const getCurrentPosition = useCallback(async (): Promise<GeolocationPosition> => {
    // Use Capacitor Geolocation plugin on native platforms for better reliability
    if (Capacitor.isNativePlatform()) {
      try {
        console.log('[useLocation] Native platform detected, using Capacitor Geolocation');

        // Request permissions first on native
        const permissionStatus = await Geolocation.checkPermissions();
        console.log('[useLocation] Permission status:', permissionStatus.location, permissionStatus.coarseLocation);

        // Handle all permission states: 'prompt', 'prompt-with-rationale', 'granted', 'denied'
        if (permissionStatus.location === 'denied') {
          throw new Error('Location permission denied');
        }

        if (permissionStatus.location !== 'granted') {
          console.log('[useLocation] Requesting location permissions...');
          const requestResult = await Geolocation.requestPermissions();
          console.log('[useLocation] Permission request result:', requestResult.location);
          if (requestResult.location === 'denied') {
            throw new Error('Location permission denied');
          }
        }

        console.log('[useLocation] Getting current position...');
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000,
        });
        console.log('[useLocation] Got position:', position.coords.latitude, position.coords.longitude);

        // Convert Capacitor position to standard GeolocationPosition format
        return {
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
          },
          timestamp: position.timestamp,
        } as GeolocationPosition;
      } catch (error: any) {
        console.error('[useLocation] Error getting location:', error.message, 'code:', error.code);
        // Re-throw with standardized error format
        const err = new Error(error.message || 'Failed to get location');
        (err as any).code = error.code || 2;
        throw err;
      }
    }

    // Fall back to browser API for web
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
      );
    });
  }, []);
  
  return {
    // State
    locations,
    uniqueCountries,
    isLoading: localLoading || storeLoading,
    error: localError || storeError,
    
    // Actions
    getUserLocations,
    getLocationById,
    getCurrentPosition,
    clearError
  };
};
export default useLocation;